/**
 * PLAT-10915 — fingerprint the base text an AI text generation read.
 *
 * Written into the provenance blob when a generation lands (this ticket) and
 * recomputed from the CURRENT base text by PLAT-10918 to flag "Transcript
 * changed". Pure and shared by both sides so the compare cannot drift from the
 * write.
 *
 * We store the hash, not the text (epic decision E5): a clip transcript is
 * easily tens of thousands of characters and the blob is persisted with the
 * video record.
 *
 * Normalization deliberately ignores the differences that don't change what
 * was said — casing, punctuation and whitespace runs — so a cosmetic
 * transcript edit doesn't read as a stale hook.
 */

/** Collapse a base text to the words it carries, in order. */
export function normalizeAITextBaseText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Stable, cheap hash of the normalized base text (FNV-1a, 32-bit) prefixed
 * with the normalized length. The length prefix costs nothing and makes the
 * collision that would matter — two different transcripts reading as
 * unchanged — vanishingly unlikely.
 *
 * Not a cryptographic hash and doesn't need to be: nothing trusts it, it only
 * answers "is this the same text I generated from?".
 */
export function fingerprintAITextBaseText(text: string): string {
  const normalized = normalizeAITextBaseText(text);
  let hash = 0x811c9dc5;
  for (let i = 0; i < normalized.length; i++) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  const digest = (hash >>> 0).toString(16).padStart(8, '0');
  return `${normalized.length.toString(36)}-${digest}`;
}
